import React, { useState, useEffect } from 'react';

import { Button, Checkbox, Form, Input } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';

import * as api from '../../api';
import { UserStatusEnum } from '../../types';
import { WabaPhoneNumber } from '../../api/account';
import { launchWhatsAppSignup, NO_AUTH_CODE_MESSAGE } from '../../facebookSdk';

type OnboardingFormProps = {
  isAdmin: boolean | null;
  partnerId: number | null;
  userOnboardingStatus: string | null;
  setConnectResponse: React.Dispatch<React.SetStateAction<string | WabaPhoneNumber[] | null>>;
};

const OnboardingForm = ({ isAdmin, partnerId, userOnboardingStatus, setConnectResponse }: OnboardingFormProps) => {
  const { token } = useParams<{ token: string }>();
  const [partnerKey, setPartnerKey] = useState<undefined | string>(token);
  const [partnerKeyConfirmation, setPartnerKeyConfirmation] = useState(false);
  const [agreeTnc, setAgreeTnc] = useState(false);
  const [signupCode, setSignupCode] = useState<null | string>(null);
  const [loading, setLoading] = useState(false);

  const onClickOnboardingButton = async () => {
    try {
      setLoading(true);
      if (userOnboardingStatus && userOnboardingStatus === UserStatusEnum.NotInitiated) {
        api.user.initiateOnboardingProcess();
      }
      // TODO: change when using real FB login
      const signupCode = await launchWhatsAppSignup();

      //const signupCode = await mockLaunchWhatsAppSignup();
      setSignupCode(signupCode);
    } catch (err) {
      console.log(err);
      if (typeof err === 'string') {
        setConnectResponse(err);
      }
      // A missing code is a configuration problem, not the user cancelling.
      if (err !== NO_AUTH_CODE_MESSAGE) {
        await api.account.userCanceledFacebookFlow();
      }
      setLoading(false);
    }
  };
  const togglePartnerKeyConfirmed = () => {
    setPartnerKeyConfirmation(!partnerKeyConfirmation);
  };
  useEffect(() => {
    if (signupCode) {
      const fetchData = async () => {
        try {
          const data = await api.account.connect(signupCode, partnerKey);
          setConnectResponse(data);
        } catch (err) {
          console.log(err);
          // Without this the page just stops loading: the signup code expires after
          // 30 seconds, so a slow or failed connect is a real case.
          setConnectResponse('We could not connect your WhatsApp account. Please try again. If it keeps happening, contact support.');
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [signupCode]);

  return (
    <Form layout="vertical">
      {!isAdmin && !partnerId && (
        <>
          <Form.Item label="Partner Key:">
            <Input
              type="email"
              placeholder="Enter Key"
              onChange={(e) => setPartnerKey(e.target.value)}
              disabled={partnerKeyConfirmation}
              value={partnerKey}
            />
          </Form.Item>
          <div className="text-muted">
            Unique key received from partner. You do not need a key for individual sign up
          </div>
          <Button
            type="primary"
            disabled={loading}
            style={{ marginTop: '10px' }}
            size="small"
            onClick={togglePartnerKeyConfirmed}
          >
            {partnerKeyConfirmation ? 'Edit' : 'Continue'}
          </Button>
        </>
      )}
      {(partnerKeyConfirmation || isAdmin || partnerId) && (
        <>
          <Form.Item>
            <Checkbox
              disabled={loading}
              checked={agreeTnc}
              onChange={(e) => {
                setAgreeTnc(e.target.checked);
              }}
            >
              I agree to terms and conditions
            </Checkbox>
          </Form.Item>
          <Button
            type="primary"
            disabled={(!partnerKeyConfirmation && !isAdmin && !partnerId) || !agreeTnc || loading}
            onClick={onClickOnboardingButton}
          >
            Connect WhatsApp Business Account / Add number {loading && <LoadingOutlined />}
          </Button>
        </>
      )}
    </Form>
  );
};

export default OnboardingForm;
